"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import { LiquidPill } from "components/ui/v2";
import {
  VendorBannerSlideV2,
} from "components/ui/v2/composites/vendor-banner-slide-v2";
import type { IVendorBanner } from "types/Vendor.types";

export interface VendorBannerCarouselV2Props {
  banners: IVendorBanner[];
  density?: "preview" | "portal";
  showPlaceholders?: boolean;
  titlePlaceholder?: string;
  descriptionPlaceholder?: string;
  /** Controlled index (config editor). */
  index?: number;
  onIndexChange?: (index: number) => void;
  /** Extra slide after banners (e.g. uploader). */
  trailingSlide?: React.ReactNode;
}

/**
 * VendorBannerCarouselV2 — banner rail with LiquidPill nav (§6.11.6).
 * Swiper is the scroll engine only; chrome is v2 Liquid controls.
 */
export function VendorBannerCarouselV2({
  banners,
  density = "portal",
  showPlaceholders = false,
  titlePlaceholder,
  descriptionPlaceholder,
  index,
  onIndexChange,
  trailingSlide,
}: VendorBannerCarouselV2Props) {
  const swiperRef = React.useRef<SwiperInstance | null>(null);
  const [internalIndex, setInternalIndex] = React.useState(0);
  const activeIndex = index ?? internalIndex;
  const totalSlides = banners.length + (trailingSlide ? 1 : 0);
  const canNavigate = totalSlides > 1;

  const setIndex = (next: number) => {
    if (index === undefined) setInternalIndex(next);
    onIndexChange?.(next);
  };

  React.useEffect(() => {
    if (swiperRef.current && typeof index === "number") {
      if (swiperRef.current.activeIndex !== index) {
        swiperRef.current.slideTo(index);
      }
    }
  }, [index]);

  const goPrev = () => {
    swiperRef.current?.slidePrev();
  };
  const goNext = () => {
    swiperRef.current?.slideNext();
  };

  if (banners.length === 0 && !trailingSlide) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Swiper
          spaceBetween={16}
          autoHeight
          observer
          observeParents
          allowTouchMove
          onSwiper={(instance) => {
            swiperRef.current = instance;
            if (typeof index === "number") instance.slideTo(index, 0);
          }}
          onSlideChange={(swiper) => {
            setIndex(swiper.realIndex);
          }}
        >
          {banners.map((banner, i) => (
            <SwiperSlide key={banner.bannerUrl || `banner-${i}`}>
              <VendorBannerSlideV2
                banner={banner}
                density={density}
                showPlaceholders={showPlaceholders}
                titlePlaceholder={titlePlaceholder}
                descriptionPlaceholder={descriptionPlaceholder}
              />
            </SwiperSlide>
          ))}
          {trailingSlide ? <SwiperSlide>{trailingSlide}</SwiperSlide> : null}
        </Swiper>

        {canNavigate ? (
          <>
            <div className="absolute left-2 top-1/2 z-[2] -translate-y-1/2">
              <LiquidPill
                as="button"
                type="button"
                variant="slate"
                appearance="tinted"
                shadow="neutral"
                shape="circle"
                size="md"
                icon={<ChevronLeft className="h-4 w-4" />}
                aria-label="Anterior"
                onClick={goPrev}
              />
            </div>
            <div className="absolute right-2 top-1/2 z-[2] -translate-y-1/2">
              <LiquidPill
                as="button"
                type="button"
                variant="slate"
                appearance="tinted"
                shadow="neutral"
                shape="circle"
                size="md"
                icon={<ChevronRight className="h-4 w-4" />}
                aria-label="Siguiente"
                onClick={goNext}
              />
            </div>
          </>
        ) : null}
      </div>

      {canNavigate ? (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <LiquidPill
              key={`dot-${i}`}
              as="button"
              type="button"
              variant={i === activeIndex ? "indigo" : "slate"}
              appearance="tinted"
              shadow="neutral"
              shape="circle"
              size="xxs"
              aria-label={`Ir al slide ${i + 1}`}
              aria-current={i === activeIndex ? "true" : undefined}
              onClick={() => {
                swiperRef.current?.slideTo(i);
                setIndex(i);
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
